import { NumericField } from "@/components/NumericField";
import { SpreadLetterRequest } from "@/components/SpreadLetterRequest";
import {
  WonBidUploadSlot,
  type HeldDocument,
} from "@/components/bond-submission/WonBidUploadSlot";
import type { BondSubmission } from "@/lib/brokerBonds";
import { SPREAD_LETTER_NOTICE } from "@/lib/wonBid";

// BRK-S421 step 2, "Bid results" — the bid tabulation as a document, or the
// next two bids, or both (BRK-S184), and the Bid Spread Letter strictly past
// 10 %, with "Request letter from client" (BRK-S331) keeping its
// save-then-send coupling: `saveFirst` is the stepper's own won save, passed
// only while the figures on screen differ from the recorded ones.

export function WonBidResultsStep({
  order,
  accept,
  heading: H,
  bidResults,
  spreadLetter,
  uploading,
  errors,
  nextClosest,
  secondClosest,
  onNextClosest,
  onSecondClosest,
  spreadRequired,
  letterFigures,
  saveFirst,
  onUpload,
}: {
  order: BondSubmission;
  accept: string;
  heading: "h3" | "h4";
  bidResults: HeldDocument | null;
  spreadLetter: HeldDocument | null;
  uploading: { bidResults: boolean; spreadLetter: boolean };
  /** Each field's inline error, by won-body field. */
  errors: Partial<Record<string, string>>;
  nextClosest: number | undefined;
  secondClosest: number | undefined;
  onNextClosest: (value: number | undefined) => void;
  onSecondClosest: (value: number | undefined) => void;
  spreadRequired: boolean;
  letterFigures: {
    finalContractPrice: number | undefined;
    nextClosestBidAmount: number | undefined;
  };
  saveFirst?: () => Promise<boolean>;
  onUpload: (slot: "bidResults" | "spreadLetter", file: File) => void;
}) {
  return (
    <>
      <p className="text-sm text-brk-dim">
        Share the bid results as a document, as the next two bids, or both. Both
        are optional.
      </p>
      <div className="flex flex-col gap-3">
        <H className="text-sm font-semibold text-brk-text">
          Upload the bid results
        </H>
        <WonBidUploadSlot
          inputLabel="Upload the bid results"
          prompt="Drag & drop the bid results, or"
          hint="The bid tabulation or results sheet."
          accept={accept}
          held={bidResults}
          uploading={uploading.bidResults}
          error={errors.bidResultsDocumentId}
          onFile={(file) => onUpload("bidResults", file)}
        />
      </div>
      <div className="flex flex-col gap-3 border-t border-brk-border pt-4">
        <H className="flex flex-wrap items-center gap-2 text-sm font-semibold text-brk-text">
          Enter the next two bids
          <span className="rounded-full bg-brk-accent/10 px-2 py-0.5 text-xs font-medium text-brk-accent">
            Recommended
          </span>
        </H>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <NumericField
            kind="currency"
            label="Next-closest bid"
            value={nextClosest}
            onChange={onNextClosest}
            optional
            error={errors.nextClosestBidAmount}
          />
          <NumericField
            kind="currency"
            label="Second-closest bid"
            value={secondClosest}
            onChange={onSecondClosest}
            optional
            error={errors.secondClosestBidAmount}
          />
        </div>
      </div>
      {spreadRequired && (
        <div
          role="note"
          data-testid="spread-letter"
          className="flex flex-col gap-3 rounded-md border border-brk-border bg-brk-bg p-3"
        >
          <p className="text-sm text-brk-text">{SPREAD_LETTER_NOTICE}</p>
          <SpreadLetterRequest
            orderId={order.id}
            accountId={order.accountId}
            spreadLetter={order.spreadLetter}
            figures={letterFigures}
            saveFirst={saveFirst}
          />
          <WonBidUploadSlot
            inputLabel="Upload the Bid Spread Letter"
            prompt="Drag & drop the Bid Spread Letter, or"
            hint="We’ll send it to Signal with the won bid."
            accept={accept}
            held={spreadLetter}
            uploading={uploading.spreadLetter}
            error={errors.spreadLetterDocumentId}
            onFile={(file) => onUpload("spreadLetter", file)}
          />
        </div>
      )}
    </>
  );
}
